import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Loader2, MoreHorizontal, Trash2, Edit2, Clock,
  User, Calendar, GripVertical, MessageSquare, CheckSquare, AlertTriangle,
  Flag, X, Send, Paperclip
} from "lucide-react";
import FileAttachments from "../components/FileAttachments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityConfig = {
  high: { label: "High", color: "bg-red-100 text-red-700 border-red-200", icon: AlertTriangle },
  medium: { label: "Medium", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Flag },
  low: { label: "Low", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Flag },
};

export default function ProjectBoardPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // Card creation
  const [addingToColumn, setAddingToColumn] = useState(null);
  const [newCardTitle, setNewCardTitle] = useState("");

  // Card detail modal
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState({});
  const [savingCard, setSavingCard] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newCheckItem, setNewCheckItem] = useState("");

  // Column management
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");

  const fetchProject = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setProject(data);
      } else {
        toast.error("Project not found");
        navigate("/projects");
      }
    } catch (e) {
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject();
    fetch(`${API}/users`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [fetchProject]);

  const getColumnCards = (columnId) => {
    if (!project?.cards) return [];
    return project.cards
      .filter(c => c.column_id === columnId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { draggableId, destination } = result;
    const destColumnId = destination.droppableId;
    const newOrder = destination.index;

    // Optimistic update
    setProject(prev => {
      if (!prev) return prev;
      const cards = [...prev.cards];
      const cardIdx = cards.findIndex(c => c.card_id === draggableId);
      if (cardIdx === -1) return prev;
      cards[cardIdx] = { ...cards[cardIdx], column_id: destColumnId, order: newOrder };
      return { ...prev, cards };
    });

    try {
      await fetch(`${API}/projects/${projectId}/cards/move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ card_id: draggableId, column_id: destColumnId, order: newOrder })
      });
    } catch (e) {
      fetchProject();
    }
  };

  const handleAddCard = async (columnId) => {
    if (!newCardTitle.trim()) return;
    try {
      const res = await fetch(`${API}/projects/${projectId}/cards`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title: newCardTitle.trim(), column_id: columnId })
      });
      if (res.ok) {
        setNewCardTitle("");
        setAddingToColumn(null);
        fetchProject();
      }
    } catch (e) {
      toast.error("Failed to add card");
    }
  };

  const openCardDetail = async (card) => {
    setSelectedCard(card);
    setEditingCard({ ...card });
    setCardModalOpen(true);
    // Fetch comments
    try {
      const res = await fetch(`${API}/projects/${projectId}/cards/${card.card_id}/comments`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (e) { /* ignore */ }
  };

  const handleSaveCard = async () => {
    setSavingCard(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/cards/${editingCard.card_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editingCard)
      });
      if (res.ok) {
        toast.success("Card updated");
        setCardModalOpen(false);
        fetchProject();
      }
    } catch (e) {
      toast.error("Failed to update card");
    } finally {
      setSavingCard(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await fetch(`${API}/projects/${projectId}/cards/${cardId}`, {
        method: "DELETE", credentials: "include"
      });
      setCardModalOpen(false);
      fetchProject();
      toast.success("Card deleted");
    } catch (e) {
      toast.error("Failed to delete card");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedCard) return;
    try {
      const res = await fetch(`${API}/projects/${projectId}/cards/${selectedCard.card_id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: newComment.trim() })
      });
      if (res.ok) {
        const comment = await res.json();
        setComments([comment, ...comments]);
        setNewComment("");
      }
    } catch (e) {
      toast.error("Failed to add comment");
    }
  };

  const toggleCheckItem = (idx) => {
    const checklist = [...(editingCard.checklist || [])];
    checklist[idx] = { ...checklist[idx], done: !checklist[idx].done };
    setEditingCard({ ...editingCard, checklist });
  };

  const addCheckItem = () => {
    if (!newCheckItem.trim()) return;
    const checklist = [...(editingCard.checklist || []), { text: newCheckItem.trim(), done: false }];
    setEditingCard({ ...editingCard, checklist });
    setNewCheckItem("");
  };

  const removeCheckItem = (idx) => {
    const checklist = (editingCard.checklist || []).filter((_, i) => i !== idx);
    setEditingCard({ ...editingCard, checklist });
  };

  const handleAddColumn = async () => {
    if (!newColumnName.trim()) return;
    try {
      await fetch(`${API}/projects/${projectId}/columns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newColumnName.trim() })
      });
      setNewColumnName("");
      setAddColumnOpen(false);
      fetchProject();
    } catch (e) {
      toast.error("Failed to add column");
    }
  };

  const handleDeleteColumn = async (columnId) => {
    if (!window.confirm("Delete this column? Cards will move to the first column.")) return;
    try {
      await fetch(`${API}/projects/${projectId}/columns/${columnId}`, {
        method: "DELETE", credentials: "include"
      });
      fetchProject();
    } catch (e) {
      toast.error("Failed to delete column");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) return null;

  const columns = (project.columns || []).sort((a, b) => a.order - b.order);
  const checklistProgress = (checklist) => {
    if (!checklist?.length) return null;
    const done = checklist.filter(i => i.done).length;
    return { done, total: checklist.length, pct: Math.round((done / checklist.length) * 100) };
  };

  return (
    <div className="h-full flex flex-col" data-testid="project-board-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/projects")} data-testid="back-to-projects">
            <ArrowLeft className="w-4 h-4 mr-1" /> Projects
          </Button>
          <h1 className="text-xl font-bold text-slate-800">{project.name}</h1>
          {project.lead_name && (
            <Badge variant="outline" className="text-xs">{project.lead_name}</Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => setAddColumnOpen(true)} data-testid="add-column-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Column
        </Button>
      </div>

      {/* Project Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 flex-1 min-h-0" data-testid="kanban-board">
          {columns.map((col) => {
            const colCards = getColumnCards(col.column_id);
            return (
              <div
                key={col.column_id}
                className="flex-shrink-0 w-[300px] flex flex-col bg-slate-50 rounded-xl border border-slate-200"
                data-testid={`column-${col.column_id}`}
              >
                {/* Column header */}
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                    <span className="font-semibold text-sm text-slate-700">{col.name}</span>
                    <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{colCards.length}</Badge>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Button
                      variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                      onClick={() => { setAddingToColumn(col.column_id); setNewCardTitle(""); }}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                    {columns.length > 1 && (
                      <Button
                        variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                        onClick={() => handleDeleteColumn(col.column_id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Cards droppable area */}
                <Droppable droppableId={col.column_id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 overflow-y-auto p-2 space-y-2 min-h-[60px] transition-colors ${
                        snapshot.isDraggingOver ? "bg-primary/5" : ""
                      }`}
                      style={{ maxHeight: "calc(100vh - 240px)" }}
                    >
                      {colCards.map((card, index) => {
                        const cp = checklistProgress(card.checklist);
                        const PriorityIcon = priorityConfig[card.priority]?.icon || Flag;
                        return (
                          <Draggable key={card.card_id} draggableId={card.card_id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`bg-white rounded-lg border border-slate-200 p-3 cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all ${
                                  snapshot.isDragging ? "shadow-lg ring-2 ring-primary/20 rotate-1" : ""
                                }`}
                                onClick={() => openCardDetail(card)}
                                data-testid={`card-${card.card_id}`}
                              >
                                <div className="flex items-start gap-2">
                                  <div {...provided.dragHandleProps} className="mt-0.5 text-slate-300 hover:text-slate-500 cursor-grab">
                                    <GripVertical className="w-3.5 h-3.5" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    {/* Labels */}
                                    {card.labels?.length > 0 && (
                                      <div className="flex gap-1 mb-1.5">
                                        {card.labels.map((l, i) => (
                                          <div key={i} className="h-1.5 w-8 rounded-full" style={{ backgroundColor: l.color || "#3b82f6" }} />
                                        ))}
                                      </div>
                                    )}
                                    <p className="text-sm font-medium text-slate-800 leading-snug">{card.title}</p>
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                      <Badge className={`${priorityConfig[card.priority]?.color} text-[10px] px-1.5 py-0 h-4 border`}>
                                        <PriorityIcon className="w-2.5 h-2.5 mr-0.5" />
                                        {priorityConfig[card.priority]?.label}
                                      </Badge>
                                      {card.due_date && (
                                        <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                          <Calendar className="w-2.5 h-2.5" />
                                          {new Date(card.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                        </span>
                                      )}
                                      {card.assigned_to_name && (
                                        <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                          <User className="w-2.5 h-2.5" />
                                          {card.assigned_to_name}
                                        </span>
                                      )}
                                    </div>
                                    {cp && (
                                      <div className="mt-2 flex items-center gap-2">
                                        <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${cp.pct}%` }} />
                                        </div>
                                        <span className="text-[10px] text-slate-400">{cp.done}/{cp.total}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}

                      {/* Inline add card */}
                      {addingToColumn === col.column_id && (
                        <div className="bg-white rounded-lg border border-primary/30 p-2 space-y-2">
                          <Input
                            value={newCardTitle}
                            onChange={(e) => setNewCardTitle(e.target.value)}
                            placeholder="Card title..."
                            className="h-8 text-sm"
                            autoFocus
                            onKeyDown={(e) => e.key === "Enter" && handleAddCard(col.column_id)}
                            data-testid="new-card-input"
                          />
                          <div className="flex gap-1">
                            <Button size="sm" className="h-7 text-xs" onClick={() => handleAddCard(col.column_id)} data-testid="add-card-btn">
                              Add
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAddingToColumn(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>

                {/* Add card button at bottom */}
                {addingToColumn !== col.column_id && (
                  <button
                    className="w-full px-3 py-2 text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex items-center gap-1.5 border-t border-slate-200"
                    onClick={() => { setAddingToColumn(col.column_id); setNewCardTitle(""); }}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add card
                  </button>
                )}
              </div>
            );
          })}

          {/* Add column button */}
          {addColumnOpen ? (
            <div className="flex-shrink-0 w-[300px] bg-slate-50 rounded-xl border border-dashed border-slate-300 p-3 space-y-2">
              <Input
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                placeholder="Column name..."
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleAddColumn()}
                data-testid="new-column-input"
              />
              <div className="flex gap-1">
                <Button size="sm" onClick={handleAddColumn} data-testid="submit-column-btn">Add</Button>
                <Button size="sm" variant="ghost" onClick={() => setAddColumnOpen(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <button
              className="flex-shrink-0 w-[300px] rounded-xl border-2 border-dashed border-slate-200 hover:border-slate-300 flex items-center justify-center gap-2 text-slate-400 hover:text-slate-500 transition-colors min-h-[100px]"
              onClick={() => setAddColumnOpen(true)}
            >
              <Plus className="w-5 h-5" /> Add Column
            </button>
          )}
        </div>
      </DragDropContext>

      {/* Card Detail Modal */}
      <Dialog open={cardModalOpen} onOpenChange={setCardModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-6">
              <span>Card Details</span>
              <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-8"
                onClick={() => selectedCard && handleDeleteCard(selectedCard.card_id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={editingCard.title || ""}
                onChange={(e) => setEditingCard({ ...editingCard, title: e.target.value })}
                data-testid="card-detail-title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editingCard.description || ""}
                onChange={(e) => setEditingCard({ ...editingCard, description: e.target.value })}
                rows={3}
                placeholder="Add a description..."
                data-testid="card-detail-desc"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={editingCard.priority || "medium"} onValueChange={(v) => setEditingCard({ ...editingCard, priority: v })}>
                  <SelectTrigger data-testid="card-detail-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assigned To</Label>
                <Select
                  value={editingCard.assigned_to || "_none"}
                  onValueChange={(v) => {
                    const u = users.find(u => u.user_id === v);
                    setEditingCard({ ...editingCard, assigned_to: v === "_none" ? null : v, assigned_to_name: u?.name || null });
                  }}
                >
                  <SelectTrigger data-testid="card-detail-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Unassigned</SelectItem>
                    {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={editingCard.due_date || ""}
                  onChange={(e) => setEditingCard({ ...editingCard, due_date: e.target.value })}
                  data-testid="card-detail-due"
                />
              </div>
              <div>
                <Label>Time Estimate (hours)</Label>
                <Input
                  type="number"
                  value={editingCard.time_estimate || ""}
                  onChange={(e) => setEditingCard({ ...editingCard, time_estimate: e.target.value })}
                  placeholder="e.g., 4"
                  data-testid="card-detail-time"
                />
              </div>
            </div>

            {/* Checklist */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-1.5 mb-2">
                <CheckSquare className="w-4 h-4" /> Checklist
              </Label>
              <div className="space-y-1.5">
                {(editingCard.checklist || []).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 group">
                    <Checkbox checked={item.done} onCheckedChange={() => toggleCheckItem(idx)} />
                    <span className={`text-sm flex-1 ${item.done ? "line-through text-slate-400" : "text-slate-700"}`}>{item.text}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500" onClick={() => removeCheckItem(idx)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newCheckItem}
                  onChange={(e) => setNewCheckItem(e.target.value)}
                  placeholder="Add item..."
                  className="h-8 text-sm"
                  onKeyDown={(e) => e.key === "Enter" && addCheckItem()}
                  data-testid="checklist-input"
                />
                <Button size="sm" className="h-8" onClick={addCheckItem}>Add</Button>
              </div>
            </div>

            {/* File Attachments */}
            <div className="border-t pt-4">
              <FileAttachments entityType="card" entityId={selectedCard?.card_id} />
            </div>

            {/* Comments */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-1.5 mb-2">
                <MessageSquare className="w-4 h-4" /> Comments ({comments.length})
              </Label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="h-8 text-sm"
                  onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                  data-testid="comment-input"
                />
                <Button size="sm" className="h-8" onClick={handleAddComment} data-testid="submit-comment-btn">
                  <Send className="w-3 h-3" />
                </Button>
              </div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {comments.map(c => (
                  <div key={c.comment_id} className="bg-slate-50 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-700">{c.user_name}</span>
                      <span className="text-[10px] text-slate-400">
                        {new Date(c.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{c.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSaveCard} disabled={savingCard} data-testid="save-card-btn">
              {savingCard ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Column Dialog (mobile fallback) */}
      <Dialog open={false}><DialogContent /></Dialog>
    </div>
  );
}
